import { useEffect, useMemo, useRef } from "react";
import type {
  Comment,
  Id,
  ScriptColumn,
  ScriptDocument,
} from "@supermanus/document-model";
import { listResolvedComments } from "@supermanus/document-model";

export interface ResolvedCommentsPanelProps {
  document: ScriptDocument;
  readOnly?: boolean;
  onClose: () => void;
  onReopen?: (commentId: Id) => void;
  onRemove: (commentId: Id) => void;
  onOpenComment?: (opts: {
    sceneId: Id;
    cueId: Id;
    column: ScriptColumn;
  }) => void;
}

type ResolvedRow = {
  comment: Comment;
  sceneId: Id | null;
  sceneLabel: string;
  column: ScriptColumn | undefined;
  columnLabel: string;
};

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString("da-DK", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function buildRows(doc: ScriptDocument): ResolvedRow[] {
  return listResolvedComments(doc).map((comment) => {
    const cue = doc.cues[comment.cueId];
    const scene = cue ? doc.scenes[cue.sceneId] : null;
    const column = comment.column;
    return {
      comment,
      sceneId: scene?.id ?? null,
      sceneLabel: scene
        ? `Scene ${scene.number}${scene.title.trim() ? ` · ${scene.title}` : ""}`
        : "Ukendt scene",
      column,
      columnLabel:
        column === "visual"
          ? "Billedside"
          : column === "audio"
            ? "Lydside"
            : "Celle",
    };
  });
}

/** Overlay listing resolved comments with reopen / delete. */
export function ResolvedCommentsPanel({
  document,
  readOnly,
  onClose,
  onReopen,
  onRemove,
  onOpenComment,
}: ResolvedCommentsPanelProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const rows = useMemo(() => buildRows(document), [document]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  return (
    <div className="sm-resolved-overlay" role="presentation">
      <button
        type="button"
        className="sm-resolved-backdrop"
        aria-label="Luk"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        className="sm-resolved-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sm-resolved-title"
        tabIndex={-1}
      >
        <header className="sm-resolved-header">
          <h2 id="sm-resolved-title">Løste kommentarer</h2>
          <button type="button" onClick={onClose}>
            Luk
          </button>
        </header>
        <p className="sm-resolved-hint">
          Løste kommentarer gemmes, indtil du sletter dem. Genåbn for at vise
          dem igen i manus.
        </p>
        {rows.length === 0 ? (
          <p className="sm-resolved-empty">Ingen løste kommentarer.</p>
        ) : (
          <ul className="sm-resolved-list">
            {rows.map((row) => {
              const { comment } = row;
              return (
                <li key={comment.id} className="sm-resolved-item">
                  <div className="sm-resolved-item-meta">
                    <strong>{row.sceneLabel}</strong>
                    <span>
                      {row.columnLabel}
                      {" · "}
                      {comment.authorName}
                      {" · "}
                      {formatWhen(comment.createdAt)}
                    </span>
                  </div>
                  {comment.quote?.trim() ? (
                    <em className="sm-resolved-quote">
                      «{comment.quote.trim()}»
                    </em>
                  ) : null}
                  <p className="sm-resolved-body">{comment.body}</p>
                  <div className="sm-resolved-actions">
                    {onOpenComment && row.sceneId && comment.column ? (
                      <button
                        type="button"
                        onClick={() =>
                          onOpenComment({
                            sceneId: row.sceneId!,
                            cueId: comment.cueId,
                            column: comment.column!,
                          })
                        }
                      >
                        Gå til
                      </button>
                    ) : null}
                    {!readOnly && onReopen ? (
                      <button
                        type="button"
                        onClick={() => onReopen(comment.id)}
                      >
                        Genåbn
                      </button>
                    ) : null}
                    {!readOnly ? (
                      <button
                        type="button"
                        className="sm-resolved-delete"
                        onClick={() => {
                          const ok = window.confirm(
                            "Slet denne løste kommentar permanent?",
                          );
                          if (ok) onRemove(comment.id);
                        }}
                      >
                        Slet
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
